import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

export interface ContactInfoData {
  salutation?: string;
  firstNames?: string;
  lastName?: string;
  businessName?: string;
  phone1?: string;
  phone2?: string;
  mobile?: string;
  fax?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  zip?: string;
  country?: string;
  email?: string;
  iban?: string;
  bankName?: string;
  bic?: string;
}

interface Props {
  value: ContactInfoData;
  onChange: (updated: ContactInfoData) => void;
  defaultExpanded?: boolean;
}

const ContactInfoForm: React.FC<Props> = ({
  value,
  onChange,
  defaultExpanded = false,
}) => {
  const set = (field: keyof ContactInfoData, val: string) =>
    onChange({ ...value, [field]: val });

  return (
    <>
      {/* Ansprechpartner */}
      <Accordion
        defaultExpanded={defaultExpanded}
        variant="outlined"
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Ansprechpartner</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                size="small"
                fullWidth
                label="Anrede"
                value={value.salutation || ""}
                onChange={(e) => set("salutation", e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                size="small"
                fullWidth
                label="Vorname(n)"
                value={value.firstNames || ""}
                onChange={(e) => set("firstNames", e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                size="small"
                fullWidth
                label="Nachname"
                value={value.lastName || ""}
                onChange={(e) => set("lastName", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Firmenname"
                value={value.businessName || ""}
                onChange={(e) => set("businessName", e.target.value)}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Adresse */}
      <Accordion
        defaultExpanded={defaultExpanded}
        variant="outlined"
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Adresse</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Adresszeile 1"
                value={value.addressLine1 || ""}
                onChange={(e) => set("addressLine1", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Adresszeile 2"
                value={value.addressLine2 || ""}
                onChange={(e) => set("addressLine2", e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                size="small"
                fullWidth
                label="PLZ"
                value={value.zip || ""}
                onChange={(e) => set("zip", e.target.value)}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                size="small"
                fullWidth
                label="Stadt"
                value={value.city || ""}
                onChange={(e) => set("city", e.target.value)}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                size="small"
                fullWidth
                label="Land"
                value={value.country || ""}
                onChange={(e) => set("country", e.target.value)}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Kontaktdaten */}
      <Accordion
        defaultExpanded={defaultExpanded}
        variant="outlined"
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Kontaktdaten</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Telefon 1"
                value={value.phone1 || ""}
                onChange={(e) => set("phone1", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Telefon 2"
                value={value.phone2 || ""}
                onChange={(e) => set("phone2", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Mobil"
                value={value.mobile || ""}
                onChange={(e) => set("mobile", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth
                label="Fax"
                value={value.fax || ""}
                onChange={(e) => set("fax", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="E-Mail"
                type="email"
                value={value.email || ""}
                onChange={(e) => set("email", e.target.value)}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Bankverbindung */}
      <Accordion
        defaultExpanded={defaultExpanded}
        variant="outlined"
        sx={{ mb: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Bankverbindung</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={7}>
              <TextField
                size="small"
                fullWidth
                label="IBAN"
                value={value.iban || ""}
                onChange={(e) => set("iban", e.target.value)}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                size="small"
                fullWidth
                label="Bankname"
                value={value.bankName || ""}
                onChange={(e) => set("bankName", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="BIC"
                value={value.bic || ""}
                onChange={(e) => set("bic", e.target.value)}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default ContactInfoForm;
